/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Canvas,
  Object as FabricObject,
  Group,
  util as fabricUtil,
  Image as FabricImage,
} from 'fabric';

import { BULLET } from '../config/constants';

import { makeColorizedUrlForObject } from './makeColorizedUrlForObject';
import { scaleImage } from './imageUtils';
import { applyLockToObject } from './lockObjects';

/**
 * Находит объект по ID в группе или на канвасе.
 * @param group Группа, в которой ищем объект.
 * @param currentId ID объекта, который нужно найти.
 * @returns Найденный объект или undefined.
 */
export const findObjectInGroup = (group: Group, currentId: string): FabricObject | undefined => {
  return group._objects.find((obj) => obj.id === currentId);
};

/**
 * Выполняет действие над объектом, если он найден в группе.
 * @param group Группа, в которой ищем объект.
 * @param currentId ID объекта.
 * @param action Действие, которое нужно выполнить с объектом.
 */
export const performActionOnObjectInGroup = (
  group: Group,
  currentId: string,
  action: (obj: FabricObject) => void,
) => {
  const obj = findObjectInGroup(group, currentId);
  if (obj) {
    action(obj);
  }
};

export const toggleGroupBackground = (canvas: Canvas, groupId: string, color = '') => {
  canvas.getObjects().forEach((obj) => {
    if (obj.id === groupId) {
      obj.set('backgroundColor', color);
    }
  });
};

export const processLoadingGroupFromConfig = async (groupConfig: any) => {
  try {
    if (!groupConfig || (groupConfig && !('objects' in groupConfig))) {
      return;
    }
    // Iterate through bullet group objects and if the image has a color field - we need to create a colorized url for this image
    for (let index = 0; index < groupConfig.objects.length; index++) {
      if (groupConfig.objects[index].type.toLowerCase() === 'image') {
        const imageObject = groupConfig.objects[index] as FabricImage;
        // If the image has a color field - store it in the object
        if (imageObject.imageColor) {
          imageObject.imageColor = groupConfig.objects[index].imageColor;
          // Create colorized url for the image
          makeColorizedUrlForObject(imageObject, groupConfig.objects[index].imageColor);
        }
      }
    }

    // Fabric.js использует координаты внутри группы относительно ЦЕНТРА группы
    // Если бэкенд передал originX: left, originY: top для группы, то координаты объектов
    // нужно конвертировать из системы left-top в систему center
    // const groupWidth = groupConfig.width || 0;
    // const groupHeight = groupConfig.height || 0;
    // const groupHasLeftTopOrigin = groupConfig.originX === 'left' && groupConfig.originY === 'top';

    // if (groupHasLeftTopOrigin && groupWidth > 0 && groupHeight > 0) {
    //   // Конвертируем координаты каждого объекта из left-top в center
    //   const centerX = groupWidth / 2;
    //   const centerY = groupHeight / 2;

    //   groupConfig.objects.forEach((obj: any) => {
    //     if (obj.left !== undefined && obj.top !== undefined) {
    //       obj.left = obj.left - centerX;
    //       obj.top = obj.top - centerY;
    //     }
    //   });

    //   console.debug('🔧 [groupUtils] Converted object coordinates from left-top to center', {
    //     groupWidth,
    //     groupHeight,
    //     centerOffset: { x: centerX, y: centerY },
    //     markerId: groupConfig.markerId,
    //   });
    // }

    // Even if there is an error loading images, we will still save the group
    let groupObjects: any[] = [];
    let isBulletWithoutIcon = true;

    try {
      groupObjects = await fabricUtil.enlivenObjects(groupConfig.objects);
    } catch (imgError) {
      console.error(
        'Error loading bullet group objects, trying to load each object separately',
        imgError,
      );

      // try to load each object separately
      groupObjects = [];
      for (let i = 0; i < groupConfig.objects.length; i++) {
        try {
          const singleObjArray = await fabricUtil.enlivenObjects([groupConfig.objects[i]]);
          if (singleObjArray && singleObjArray.length > 0) {
            groupObjects.push(singleObjArray[0]);
          }
        } catch (singleObjError) {
          console.error(`Skipping object ${i} in bullet group due to load error:`, singleObjError);
          // skip this object and continue with others
        }
      }

      // If we couldn't load any objects, skip this group
      if (groupObjects.length === 0) {
        console.warn('Could not load any objects for this bullet group, skipping');
        return;
      }
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    const group = new Group(groupObjects as unknown as FabricObject[], {
      left: groupConfig.left,
      top: groupConfig.top,
      width: groupConfig.width,
      height: groupConfig.height,
      layer: groupConfig.layer,
      zIndex: groupConfig.zIndex,
      id: groupConfig.id,
      name: groupConfig.name,
      category: groupConfig.category,
      lockScalingX: groupConfig.lockScalingX,
      lockScalingY: groupConfig.lockScalingY,
      lockRotation: groupConfig.lockRotation,
      hasControls: groupConfig.hasControls,
      originX: groupConfig.originX,
      originY: groupConfig.originY,
      // === ПОЛЯ МАРКЕРОВ ===
      markerId: groupConfig.markerId,
      entityId: groupConfig.entityId,
      isBulletWithoutIcon: groupConfig.isBulletWithoutIcon,
    });

    // Fabric.js пересчитывает размеры группы при создании, но нам нужны оригинальные
    // if (groupConfig.width !== undefined && groupConfig.height !== undefined) {
    //   const fabricCalculatedSize = { width: group.width, height: group.height };

    //   // Восстанавливаем оригинальные размеры от бэкенда
    //   group.set({
    //     width: groupConfig.width,
    //     height: groupConfig.height,
    //   });

    //   console.debug('📏 [groupUtils] Fixed group dimensions:', {
    //     groupId: group.id,
    //     fabricCalculated: fabricCalculatedSize,
    //     restoredFromConfig: { width: groupConfig.width, height: groupConfig.height },
    //   });
    // }

    // Логируем если это Group с маркером
    if (groupConfig.markerId) {
      console.debug('✅ [groupUtils] Created Group with markerId:', {
        groupId: group.id,
        markerId: groupConfig.markerId,
        entityId: groupConfig.entityId,
        hasMarkerIdOnGroup: !!(group as any).markerId,
        groupMarkerIdValue: (group as any).markerId,
      });
    }

    // If the group has the isLocked property, apply the lock to the group
    if (groupConfig.isLocked) {
      applyLockToObject(group, true);
    }
    group.subTargetCheck = true; // Enable sub-object checking in the group (clicks inside the group)
    // Assign parameters to each object within the group
    for (let index = 0; index < groupObjects.length; index++) {
      const obj = group.getObjects()[index];
      obj.id = groupConfig.objects[index].id;
      obj.category = groupConfig.objects[index].category;
      obj.idGroup = groupConfig.objects[index].idGroup;
      obj.noCopy = groupConfig.objects[index].noCopy;
      obj.originX = groupConfig.objects[index].originX;
      obj.originY = groupConfig.objects[index].originY;
      obj.shape_type = groupConfig.objects[index].shape_type;
      obj.clip_path = groupConfig.objects[index].clip_path;
      obj.inner_id = groupConfig.objects[index].inner_id;

      // Apply lock state to objects inside the group
      if (groupConfig.objects[index].isLocked === true) {
        applyLockToObject(obj, true);
      }
      if (obj?.type?.toLowerCase() === 'image') {
        if (obj?.category?.toLowerCase() === BULLET.IMAGE_ITEM) {
          isBulletWithoutIcon = false;
        }
        const imageObject = obj as FabricImage;
        //It is need for backend. type image.
        imageObject.media_type = groupConfig.objects[index].media_type;
        imageObject.source_ext = groupConfig.objects[index].source_ext;
        imageObject.source_width = groupConfig.objects[index].source_width; //width of the image
        imageObject.source_height = groupConfig.objects[index].source_height; //height of the image
        imageObject.crop = groupConfig.objects[index].crop; //crop of the image

        //If the flag exists and is true, scale the image
        if (!('isNeedScaleImgForFirstRender' in obj) || obj.isNeedScaleImgForFirstRender) {
          scaleImage(imageObject, imageObject.width, imageObject.height);
        }
      }
      if (obj?.type?.toLocaleLowerCase() === 'path') {
        obj.set('shape_type', groupConfig.objects[index].shape_type);
      }
      if (obj?.type?.toLocaleLowerCase() === 'textbox') {
        obj.set('margin', groupConfig.objects[index].margin);
        obj.set('space', groupConfig.objects[index].space);
        if (
          groupConfig.objects[index].originalText !== undefined &&
          groupConfig.objects[index].originalText !== null
        ) {
          obj.set('originalText', groupConfig.objects[index].originalText);
        } else {
          obj.set('originalText', groupConfig.objects[index].text);
        }
      }
    }
    group.isBulletWithoutIcon = isBulletWithoutIcon;
    // Add group to the appropriate layer
    return group;
  } catch (e) {
    console.error('Error loading bullet', e);
  }
};

export const isBullet = (obj: FabricObject) => {
  return (
    (obj?.category?.toLowerCase() === BULLET.DEFAULT && obj?.type?.toLowerCase() === 'group') ??
    false
  );
};

export const isBulletTextItem = (obj: FabricObject) => {
  return (
    (obj?.category?.toLowerCase() === BULLET.TEXT_ITEM && obj?.type?.toLowerCase() === 'textbox') ??
    false
  );
};

export const isBulletImageItem = (obj: FabricObject) => {
  return (
    (obj?.category?.toLowerCase() === BULLET.IMAGE_ITEM && obj?.type?.toLowerCase() === 'image') ??
    false
  );
};
